import { useContext } from 'react';
import { GlobalSiteDataContext } from '../../../context';
import { METRIC_FORMATTERS } from '../../../hooks/useMetrics';
import type { Metrics } from '../../../context';

export interface UseFirebaseMetricsReturn {
    replaceVariables: (content: string | undefined | null) => string;
}

// Replaces {{variableName}} placeholders in content with formatted metric values
function replaceVariables(
    content: string | undefined | null,
    variables: Metrics,
    formatters: typeof METRIC_FORMATTERS = METRIC_FORMATTERS
): string {
    if (!content || typeof content !== 'string') {
        return content || '';
    }

    let processedContent = content;

    Object.entries(variables).forEach(([key, value]) => {
        const formatter = formatters[key as keyof Metrics] || ((v: number) => String(v));
        const formattedValue = formatter(value);
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        processedContent = processedContent.replace(regex, formattedValue);
    });

    return processedContent;
}

/**
 * Custom hook that provides metrics and variable replacement for Builder components.
 * This hook consumes metrics from GlobalSiteDataContext (calculated once in GlobalContextProvider)
 * and provides a replaceVariables function for Builder components to replace {{variableName}}
 * placeholders in content.
 */
export function useFirebaseMetrics(): UseFirebaseMetricsReturn {
    const siteData = useContext(GlobalSiteDataContext);

    const metrics = siteData?.metrics || {
        clinicCount: 0,
        totalReviews: 0,
        stateCount: 0,
        treatmentsPerMonth: 0,
    };

    return {
        replaceVariables: (content: string | undefined | null) => replaceVariables(content, metrics),
    };
}
